// src/components/business/AIChatButton.tsx
// Floating AI chat widget — opens as a modal panel (bottom-right),
// dashboard stays fully visible and interactive behind it.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles, PlusCircle, ArrowUp, X, Minus } from 'lucide-react';
import apiClient from '@/services/apiClient';

interface Props { businessId: string; }

interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

// ── Typing indicator ──────────────────────────────────────────────────────────
const TypingIndicator: React.FC = () => (
  <div className="mb-3 self-start">
    <div className="px-3.5 py-3 rounded-[18px] rounded-bl-[4px] border bg-white dark:bg-dark-card border-light-border dark:border-dark-border max-w-[160px]">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles size={13} className="text-primary" />
        <span className="text-[12px] font-semibold text-primary">Majengo AI</span>
      </div>
      <div className="flex items-center gap-1.5 py-0.5">
        {[0, 150, 300].map((delay, i) => (
          <span
            key={i}
            className="w-1.5 h-1.5 rounded-full inline-block bg-light-subtle dark:bg-dark-subtle"
            style={{ animation: `ai-bounce 1.2s ${delay}ms infinite` }}
          />
        ))}
      </div>
    </div>
  </div>
);

// ── Inline markdown renderer ──────────────────────────────────────────────────
const MarkdownText: React.FC<{ text: string; isUser: boolean }> = ({ text, isUser }) => {
  const color = isUser ? 'white' : undefined;
  return (
    <div className="flex flex-col gap-0.5">
      {text.split('\n').map((line, i) => {
        const h3 = line.match(/^###\s+(.+)/);
        const h2 = line.match(/^##\s+(.+)/);
        const h1 = line.match(/^#\s+(.+)/);
        if (h3) return <span key={i} className="font-bold mt-1.5 text-[14px]" style={{ color }}>{h3[1]}</span>;
        if (h2) return <span key={i} className="font-bold mt-1.5 text-[15px]" style={{ color }}>{h2[1]}</span>;
        if (h1) return <span key={i} className="font-bold mt-1.5 text-[16px]" style={{ color }}>{h1[1]}</span>;

        const bullet = line.match(/^[\s]*[•\-\*][\s]+(.+)/);
        if (bullet) return (
          <span key={i} className="text-[13px] leading-5" style={{ color }}>
            {'• '}{renderInline(bullet[1], isUser)}
          </span>
        );

        const numbered = line.match(/^[\s]*(\d+)\.[\s]+(.+)/);
        if (numbered) return (
          <span key={i} className="text-[13px] leading-5" style={{ color }}>
            <strong>{numbered[1]}.</strong>{' '}{renderInline(numbered[2], isUser)}
          </span>
        );

        if (!line.trim()) return <br key={i} />;
        return <span key={i} className="text-[13px] leading-5" style={{ color }}>{renderInline(line, isUser)}</span>;
      })}
    </div>
  );
};

function renderInline(text: string, isUser: boolean) {
  const color = isUser ? 'white' : undefined;
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    const bold = part.match(/^\*\*(.+)\*\*$/);
    if (bold) return <strong key={i} style={{ color }}>{bold[1]}</strong>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

// ── Main component ────────────────────────────────────────────────────────────
const AIChatButton: React.FC<Props> = ({ businessId }) => {
  const [open, setOpen]         = useState(false);
  const [minimised, setMin]     = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput]       = useState('');
  const [typing, setTyping]     = useState(false);
  const bottomRef  = useRef<HTMLDivElement>(null);
  const inputRef   = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () =>
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);

  const startNew = useCallback(() => {
    setMessages([{
      id: '1',
      role: 'ai',
      content: "Hello! I'm your Majengo AI assistant. I can help you with:\n\n• Savings plans and financial goals\n• Inventory management and alerts\n• Supplier recommendations\n• Business growth strategies\n\nWhat would you like to discuss today?",
      timestamp: new Date(),
    }]);
  }, []);

  useEffect(() => { if (open && messages.length === 0) startNew(); }, [open]);
  useEffect(() => { if (open && !minimised) scrollToBottom(); }, [messages, typing]);

  const sendMessage = async () => {
    if (!input.trim() || typing) return;
    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: input.trim(), timestamp: new Date() };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setTyping(true);
    scrollToBottom();

    let attempt = 0;
    while (attempt < 3) {
      attempt++;
      try {
        const res = await apiClient.post(
          `/businesses/${businessId}/ai/chat`,
          { message: userMsg.content, conversation: next.slice(-5) },
          { timeout: 120000 }
        );
        let aiText = res?.data?.insights || res?.data?.data?.insights || res?.data;
        if (!aiText || typeof aiText !== 'string') {
          aiText = 'I can help you with savings plans, inventory management, and supplier suggestions for your business.';
        }
        await new Promise(r => setTimeout(r, Math.max(600, Math.min(aiText.length * 15, 2500))));
        setMessages(prev => [...prev, { id: Date.now().toString(), role: 'ai', content: aiText, timestamp: new Date() }]);
        break;
      } catch (err: any) {
        const isNetwork = err?.message?.toLowerCase().includes('network') || err?.message?.toLowerCase().includes('timeout');
        if (attempt >= 3 || !isNetwork) {
          await new Promise(r => setTimeout(r, 600));
          setMessages(prev => [...prev, { id: Date.now().toString(), role: 'ai', content: "I'm having trouble connecting right now. Please try again in a moment.", timestamp: new Date() }]);
          break;
        }
        await new Promise(r => setTimeout(r, Math.min(5000, 500 * Math.pow(2, attempt))));
      }
    }
    setTyping(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      <style>{`
        @keyframes ai-bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40%            { transform: translateY(-5px); }
        }
        @keyframes ai-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 8px 32px rgba(79,70,229,0.45); }
          50%       { transform: scale(1.1); box-shadow: 0 12px 40px rgba(79,70,229,0.6); }
        }
        @keyframes ai-slide-up {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>

      {/* ── FAB — bottom-left, only when closed ── */}
      {!open && (
        <button
          onClick={() => { setOpen(true); setMin(false); }}
          className="fixed bottom-8 left-8 z-50 w-16 h-16 rounded-full flex items-center justify-center text-white"
          style={{ backgroundColor: '#4F46E5', animation: 'ai-pulse 3s ease-in-out infinite' }}
          title="Open AI Assistant"
        >
          <Sparkles size={26} />
        </button>
      )}

      {/* ── Floating modal panel — bottom-right, dashboard stays visible ── */}
      {open && (
        <div
          className="fixed z-50 flex flex-col"
          style={{
            bottom: 24,
            right: 24,
            width: 'min(420px, calc(100vw - 48px))',
            // full height when open, collapsed to header only when minimised
            height: minimised ? 'auto' : 'min(600px, calc(100vh - 120px))',
            borderRadius: 20,
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.10)',
            animation: 'ai-slide-up 0.22s ease-out',
            backgroundColor: 'var(--ai-bg, #F8FAFC)',
          }}
        >

          {/* ── Header ── */}
          <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0 bg-white dark:bg-dark-card border-b border-light-border dark:border-dark-border">
            {/* AI avatar */}
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
              <Sparkles size={16} className="text-primary" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-light-text dark:text-dark-text leading-none">Majengo AI</p>
              <p className="text-[11px] text-light-subtle dark:text-dark-subtle mt-0.5">
                {typing ? 'Typing…' : 'Always here to help'}
              </p>
            </div>

            {/* New conversation */}
            <button
              onClick={startNew}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
              title="New conversation"
            >
              <PlusCircle size={17} className="text-primary" />
            </button>

            {/* Minimise */}
            <button
              onClick={() => setMin(m => !m)}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
              title={minimised ? 'Expand' : 'Minimise'}
            >
              <Minus size={17} className="text-light-subtle dark:text-dark-subtle" />
            </button>

            {/* Close */}
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
              title="Close"
            >
              <X size={17} className="text-light-subtle dark:text-dark-subtle" />
            </button>
          </div>

          {/* ── Body — hidden when minimised ── */}
          {!minimised && (
            <>
              {/* Messages */}
              <div
                className="flex-1 overflow-y-auto px-4 py-3 flex flex-col"
                style={{ backgroundColor: '#F8FAFC' }}
              >
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center flex-1 py-10 px-6 text-center">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                      <Sparkles size={32} className="text-primary" />
                    </div>
                    <p className="text-base font-bold text-light-text dark:text-dark-text mb-1">AI Business Assistant</p>
                    <p className="text-xs text-light-subtle dark:text-dark-subtle leading-5">
                      Get insights on savings, inventory, suppliers, and growth strategies
                    </p>
                  </div>
                )}

                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`mb-3 max-w-[88%] px-3.5 py-3 rounded-[18px] ${
                      msg.role === 'user'
                        ? 'self-end rounded-br-[4px] bg-primary'
                        : 'self-start rounded-bl-[4px] bg-white dark:bg-dark-card border border-light-border dark:border-dark-border'
                    }`}
                  >
                    {msg.role === 'ai' && (
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Sparkles size={12} className="text-primary" />
                        <span className="text-[11px] font-semibold text-primary">Majengo AI</span>
                      </div>
                    )}
                    <MarkdownText text={msg.content} isUser={msg.role === 'user'} />
                    <p className={`text-[10px] mt-1 text-right ${msg.role === 'user' ? 'text-white/70' : 'text-light-subtle dark:text-dark-subtle'}`}>
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}

                {typing && <TypingIndicator />}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="px-3 py-2.5 border-t border-light-border dark:border-dark-border bg-white dark:bg-dark-card flex-shrink-0">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Message Majengo AI…"
                    disabled={typing}
                    rows={1}
                    maxLength={2000}
                    className="flex-1 resize-none rounded-2xl px-3.5 py-2.5 text-sm border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text placeholder-light-placeholder dark:placeholder-dark-placeholder focus:outline-none focus:ring-2 focus:ring-primary/20"
                    style={{ maxHeight: 100, lineHeight: '20px' }}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!input.trim() || typing}
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mb-0.5 transition-colors disabled:opacity-40"
                    style={{ backgroundColor: input.trim() && !typing ? '#4F46E5' : '#E2E8F0' }}
                  >
                    <ArrowUp size={18} color="white" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default AIChatButton;
